import Cookies from "js-cookie";
import { clientTabToken, isClientEnvironment } from "./clientEnvironment";

/**
 * The one place the app reads its bearer credential from.
 *
 * Authentication is header-based: every request carries the Stytch session as
 * `Authorization: Bearer <token>`, and the backend reads no cookies at all. The
 * `stytch_session` cookie is therefore just a storage location, not the
 * mechanism — which is what lets an embedded frame supply the same credential
 * from memory instead.
 *
 * A cross-site iframe cannot read the cookie: the Stytch SDK writes it
 * `SameSite=Lax`, and Lax cookies are unavailable in a third-party frame in
 * every current browser. In that context the session arrives by postMessage
 * from a top-level sign-in popup (see `embedSession.ts`) and is kept here, in
 * memory and in storage (see `STORAGE_KEY`).
 *
 * Memory wins over the cookie when both are present. A frame that has been
 * handed a session is authoritatively that session's; a stale cookie from an
 * unrelated first-party visit must not override it.
 *
 * See #11021.
 */

const COOKIE_NAME = "stytch_session";

/**
 * Where an embedded frame's session is kept so it survives a reload and a new
 * tab, for as long as the server accepts it: the same lifetime as the web
 * app's session (#15202). Browsers partition a cross-site frame's storage by
 * top-level site, so one embedding host cannot see another's session.
 *
 * Written by the embed handshake. The Finpilot Notetaker app's shell writes
 * `sessionStorage` before the page loads (#9163, `preloadScript` in
 * `fp-notetaker-mobile/src/web/shell.ts`), so that is still read. A
 * first-party tab uses the cookie and never writes here.
 */
const STORAGE_KEY = "fp_embed_session";

function readStoredToken(): string | null {
  try {
    return (
      localStorage.getItem(STORAGE_KEY) ?? sessionStorage.getItem(STORAGE_KEY)
    );
  } catch {
    // Storage can throw outright when a browser blocks third-party storage.
    // The frame then behaves as it did before persistence existed: it works
    // for this page load and asks for sign-in again after a refresh.
    return null;
  }
}

function persistToken(token: string | null): void {
  try {
    if (token === null) {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, token);
    }
  } catch {
    // See readStoredToken.
  }
}

let inMemoryToken: string | null = readStoredToken();

const subscribers = new Set<() => void>();

function notify(): void {
  subscribers.forEach((cb) => cb());
}

export function getSessionToken(): string | null {
  // A client tab (#10718) authenticates only as the collaborator its grant
  // activated, with the token it loaded with: never the staff member's own
  // Stytch session, which the cookie on this same address would supply, and
  // never a token another tab put in place since.
  if (isClientEnvironment()) return clientTabToken();
  if (inMemoryToken) return inMemoryToken;
  return Cookies.get(COOKIE_NAME) ?? null;
}

/** Whether the session came from a popup handshake rather than the cookie. */
export function hasInMemorySessionToken(): boolean {
  return inMemoryToken !== null;
}

export function setSessionToken(token: string): void {
  inMemoryToken = token;
  persistToken(token);
  notify();
}

export function clearSessionToken(): void {
  inMemoryToken = null;
  persistToken(null);
  notify();
}

/** Subscribe to in-memory token changes. Returns an unsubscribe function. */
export function subscribeSessionToken(onChange: () => void): () => void {
  subscribers.add(onChange);
  return () => {
    subscribers.delete(onChange);
  };
}
